import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { DatasourceUpstreamFlowsQuery, DatasourceUpstreamFlowsQueryVariables } from '../../../gen-src/queries/DatasourceUpstreamFlowsQuery';
import { DatasourceUpstreamFlowsWithoutPersonalSpaceQuery, DatasourceUpstreamFlowsWithoutPersonalSpaceQueryVariables } from '../../../gen-src/queries/DatasourceUpstreamFlowsWithoutPersonalSpaceQuery';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  UpstreamFlowsFilteredByFieldQuery,
  UpstreamFlowsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/UpstreamFlowsFilteredByFieldQuery';
import {
  UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery,
  UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceUpstreamFlowsQueryGql } from '../../queries/DatasourceUpstreamFlowsQueryGql';
import { DatasourceUpstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/DatasourceUpstreamFlowsWithoutPersonalSpaceQueryGql';
import { UpstreamFlowsFilteredByFieldQueryGql } from '../../queries/UpstreamFlowsFilteredByFieldQueryGql';
import { UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = DatasourceUpstreamFlowsQueryVariables;
type QueryResults = DatasourceUpstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatasourceUpstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatasourceUpstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = UpstreamFlowsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamFlowsFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return (
    resultData &&
    resultData.publishedDatasources &&
    resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].upstreamFlowsConnection
  );
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return (
    resultData &&
    resultData.publishedDatasources &&
    resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].upstreamFlowsConnection
  );
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  return (
    resultData &&
    resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] &&
    resultData.fieldsConnection.nodes[0].upstreamFlowsConnection
  );
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return (
    resultData &&
    resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] &&
    resultData.fieldsConnection.nodes[0].upstreamFlowsConnection
  );
}

export type DatasourceUpstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly rowCount: number | null;
};

export function DatasourceUpstreamFlowsQueryGrid(props: DatasourceUpstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={UpstreamFlowsFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatasourceUpstreamFlowsQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatasourceUpstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
